import React, {useCallback, useEffect, useState} from 'react';
import {SearchInput} from '../../search/SearchInput';
import {useUiStore} from '../../../common/stores/useUiStore';
import {OrganizerDto} from '../../../generated/models/OrganizerDto';
import {OrganizerClaimGroup} from '../../card/OrganizerClaimGroup';
import {
  ClaimPlaceStatus,
  fetchPendingClaims,
  updatePlaceClaimStatus,
} from '../../../common/services/claims';

export const ApprovePlacesScreen: React.FC = () => {
  const setIsLoading = useUiStore(state => state.setIsLoading);
  const [claims, setClaims] = useState<OrganizerDto[] | null>(null);
  const [searchText, setSearchText] = useState('');
  const [loadError, setLoadError] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const loadPendingClaims = useCallback(async () => {
    setIsLoading(true);
    setLoadError(false);
    try {
      setClaims(await fetchPendingClaims());
    } catch (error) {
      console.error('Error fetching pending claims:', error);
      // Not "nothing pending": say the list could not be loaded.
      setClaims(null);
      setLoadError(true);
    } finally {
      setIsLoading(false);
    }
  }, [setIsLoading]);

  useEffect(() => {
    loadPendingClaims();
  }, [loadPendingClaims]);

  const changeStatus = useCallback(
    async (placeId: string, status: ClaimPlaceStatus) => {
      setIsLoading(true);
      setActionError(null);
      const success = await updatePlaceClaimStatus(placeId, status);
      if (success) {
        await loadPendingClaims();
      } else {
        setIsLoading(false);
        setActionError('The change could not be saved. Try again.');
      }
    },
    [loadPendingClaims, setIsLoading],
  );

  const handleApprove = (placeId: string) =>
    changeStatus(placeId, ClaimPlaceStatus.Approved);

  const handleDecline = (placeId: string) =>
    changeStatus(placeId, ClaimPlaceStatus.NotApproved);

  const list = claims ?? [];
  const q = searchText.trim().toLowerCase();

  const filtered: OrganizerDto[] = !q
    ? list
    : list
        .map(org => {
          const orgMatch =
            (org.name ?? '').toLowerCase().includes(q) ||
            (org.companyName ?? '').toLowerCase().includes(q);

          if (orgMatch) {
            return org;
          }

          const places = (org.places ?? []).filter(
            p =>
              (p.name ?? '').toLowerCase().includes(q) ||
              (p.description ?? '').toLowerCase().includes(q),
          );

          return places.length ? {...org, places} : null;
        })
        .filter((o): o is OrganizerDto => o !== null);

  const hasClaims = list.length > 0;

  const emptyMessage =
    list.length === 0
      ? 'There are no places pending approval.'
      : 'No places or organizers match your search.';

  return (
    <div
      style={{
        backgroundColor: '#1a1a1a',
        minHeight: '100vh',
        padding: '40px 20px',
      }}>
      <div style={{maxWidth: '800px', margin: '0 auto'}}>
        <h1 style={{color: '#fff', textAlign: 'center', marginBottom: '30px'}}>
          Approve Places
        </h1>

        {hasClaims && (
          <div
            style={{
              marginBottom: '40px',
              display: 'flex',
              justifyContent: 'center',
            }}>
            <SearchInput
              placeholder="Search places or organizers..."
              onSearch={setSearchText}
              debounceMs={0}
            />
          </div>
        )}

        {actionError && (
          <p style={{color: '#ff4d4d', textAlign: 'center'}}>{actionError}</p>
        )}

        {loadError && (
          <div style={{textAlign: 'center', marginTop: '40px'}}>
            <p style={{color: '#ff4d4d', fontSize: '1.1rem'}}>
              Could not load the pending claims.
            </p>
            <button onClick={loadPendingClaims} style={retryButtonStyle}>
              Try again
            </button>
          </div>
        )}

        <div style={{display: 'flex', flexDirection: 'column', gap: '20px'}}>
          {filtered.map(org => (
            <OrganizerClaimGroup
              key={org.id}
              organizer={org}
              onApprove={handleApprove}
              onDecline={handleDecline}
            />
          ))}

          {claims !== null && filtered.length === 0 && (
            <p
              style={{
                color: '#666',
                textAlign: 'center',
                fontSize: '1.3rem',
                marginTop: '40px',
              }}>
              {emptyMessage}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const retryButtonStyle: React.CSSProperties = {
  padding: '10px 18px',
  borderRadius: '8px',
  fontWeight: 'bold',
  fontSize: '0.85rem',
  cursor: 'pointer',
  backgroundColor: '#2a2a2a',
  color: '#fff',
  border: '1px solid #333',
};
